import {
    AlertCircle,
    Calendar,
    ClipboardCheck,
    ListChecks,
    type LucideIcon,
    PhoneOutgoing,
    Search,
    SlidersHorizontal,
    Users,
} from 'lucide-react';

type StarterPrompt = {
    readonly icon: LucideIcon;
    readonly label: string;
    readonly seedText: string;
};

const DOCKED_STARTER_PROMPTS: ReadonlyArray<StarterPrompt> = [
    {
        icon: Calendar,
        label: "What's on the schedule today?",
        seedText: 'What shifts are on the schedule today?',
    },
    {
        icon: AlertCircle,
        label: 'Find open shifts',
        seedText: 'Show me open shifts this week',
    },
    {
        icon: Users,
        label: 'Find a caregiver',
        seedText: 'Find a caregiver for ',
    },
    {
        icon: PhoneOutgoing,
        label: 'Start an outreach',
        seedText: 'Help me start an outreach for ',
    },
];

const CALLOUT_STARTER_PROMPTS: ReadonlyArray<StarterPrompt> = [
    {
        icon: Search,
        label: 'Find more caregivers',
        seedText: 'Find more caregivers for this open shift',
    },
    {
        icon: ListChecks,
        label: 'Explain this list',
        seedText: 'Explain the caregivers in this list',
    },
    {
        icon: SlidersHorizontal,
        label: 'Broaden the search',
        seedText: 'Broaden the caregiver search for this open shift',
    },
];

type AgentSidebarEmptyStateProps = {
    readonly onSeedPrompt: (seedText: string) => void;
    readonly variant?: 'general' | 'callout';
    readonly showReviewMyRuns?: boolean;
    readonly onReviewMyRuns?: () => void;
};

export function AgentSidebarEmptyState({
    onSeedPrompt,
    variant = 'general',
    showReviewMyRuns = false,
    onReviewMyRuns,
}: AgentSidebarEmptyStateProps) {
    const isCalloutVariant = variant === 'callout';
    const starterPrompts = isCalloutVariant
        ? CALLOUT_STARTER_PROMPTS
        : DOCKED_STARTER_PROMPTS;
    const showReviewMyRunsRow =
        !isCalloutVariant && showReviewMyRuns && onReviewMyRuns !== undefined;

    return (
        <div className="flex flex-col gap-5 px-4 pb-2">
            <div className="flex items-start gap-2.5">
                <img
                    src="/icon.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-8 w-8 rounded-full bg-[#F0F5F5] p-1.5"
                />
                <div className="min-w-0">
                    <p className="text-[15px] font-semibold text-[#002429]">
                        {isCalloutVariant
                            ? 'Phoebe can help with this open shift'
                            : 'How can I help you today?'}
                    </p>
                    {isCalloutVariant ? (
                        <p className="mt-0.5 text-sm text-[#42595E]">
                            Ask for more caregivers, context on the current
                            list, or ways to widen the search.
                        </p>
                    ) : null}
                </div>
            </div>
            <div className="-mx-1 flex flex-col gap-0.5">
                {starterPrompts.map(({ icon: Icon, label, seedText }) => (
                    <button
                        key={label}
                        type="button"
                        onClick={() => {
                            onSeedPrompt(seedText);
                        }}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left text-sm text-[#42595E] transition-colors hover:bg-[#F0F5F5] hover:text-[#002429]"
                    >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="flex-1 truncate">{label}</span>
                    </button>
                ))}
                {showReviewMyRunsRow ? (
                    <button
                        type="button"
                        onClick={onReviewMyRuns}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left text-sm text-[#42595E] transition-colors hover:bg-[#F0F5F5] hover:text-[#002429]"
                    >
                        <ClipboardCheck className="h-4 w-4 shrink-0" />
                        <span className="flex-1 truncate">Review my chats</span>
                    </button>
                ) : null}
            </div>
        </div>
    );
}
